import _AlertInformationIcon from "@adsk/alloy-react-icon/es/lib/AlertInformationIcon";
import _styled from "styled-components";
const _excluded = ["className", "style", "renderIcon", "size", "placement", "message", "container", "shouldOpenOnClick", "onClick", "variant", "overlayStyle", "delayHide", "delayShow", "onShow", "onHide"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop, useOnClickOutside } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Flyout from './Flyout';
const InfoPopover = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      className,
      style,
      renderIcon = _AlertInformationIcon.renderIcon,
      size = 16,
      placement = Flyout.PLACEMENTS.BOTTOM,
      message,
      container,
      shouldOpenOnClick = true,
      onClick = noop,
      variant,
      overlayStyle,
      delayHide,
      delayShow,
      onShow: onShowProp,
      onHide: onHideProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const overlayRef = useRef(null);
  const targetRef = useRef(null);
  const [isClicked, setIsClicked] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const isOpen = isClicked || isMouseOver;
  const timeoutHideRef = useRef();
  const timeoutShowRef = useRef();
  const clearShowAndHideTimeouts = useCallback(() => {
    clearTimeout(timeoutHideRef.current);
    clearTimeout(timeoutShowRef.current);
  }, []);
  function handleClickOutside() {
    clearShowAndHideTimeouts();
    timeoutHideRef.current = setTimeout(() => {
      setIsClicked(false);
      setIsMouseOver(false);
    }, delayHide);
  }
  function onClickClose(e) {
    if (!isClicked && shouldOpenOnClick) {
      clearShowAndHideTimeouts();
      timeoutShowRef.current = setTimeout(() => {
        setIsClicked(true);
      }, delayShow);
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  }
  useOnClickOutside(Boolean(shouldOpenOnClick && isClicked), handleClickOutside, [targetRef, overlayRef]);
  const onShow = () => {
    onShowProp === null || onShowProp === void 0 ? void 0 : onShowProp();
    setIsMouseOver(true);
  };
  const onHide = () => {
    onHideProp === null || onHideProp === void 0 ? void 0 : onHideProp();
    setIsMouseOver(false);
  };
  const renderFlyoutBody = useCallback(props => {
    const variantContainerPadding = variant === Flyout.VARIANTS.DARK ? '8px 12px' : '16px';
    return /*#__PURE__*/React.createElement(Flyout.Body, _extends({}, props, {
      padding: variantContainerPadding
    }));
  }, [variant]);
  return (
    /*#__PURE__*/
    // Will be fixed in https://jira.autodesk.com/browse/DSGN-1836
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    React.createElement(_StyledDiv, {
      ref: ref,
      className: className,
      onClick: onClickClose,
      $_css: [style]
    }, /*#__PURE__*/React.createElement(Flyout, _extends({
      overlayRef: overlayRef,
      content: message,
      show: isOpen,
      container: container,
      renderBody: renderFlyoutBody,
      placement: placement,
      delayHide: delayHide || 100,
      delayShow: delayShow,
      variant: variant,
      style: overlayStyle,
      onShow: onShow,
      onHide: onHide,
      trigger: Flyout.TRIGGER_EVENTS.HOVER
    }, props), renderIcon === null || renderIcon === void 0 ? void 0 : renderIcon({
      ref: targetRef,
      size,
      style: {
        cursor: 'pointer',
        outline: 'none',
        color: theme.tokens.colors.icon.information.value
      },
      'aria-hidden': false
    })))
  );
});
InfoPopover.displayName = 'InfoPopover';
InfoPopover.propTypes = {
  /**
   * className to add to the InfoPopover component
   */
  className: PropTypes.string,
  /**
   * style to add to the InfoPopover component
   */
  style: stylePropType,
  /**
   * The icon square size
   */
  size: PropTypes.number,
  /**
   * A node to be shown on hover.
   */
  message: PropTypes.any.isRequired,
  /**
   * Optional function to run when the info icon is pressed. Passes the click event target
   */
  onClick: PropTypes.func,
  /**
   * Optional parameter to specify if InfoPopover will stay opened on click.
   */
  shouldOpenOnClick: PropTypes.bool,
  /**
   * The icon to be displayed for the info
   */
  renderIcon: PropTypes.func,
  /**
   * The icon to be displayed for the info
   */
  placement: PropTypes.oneOf(Object.values(Flyout.PLACEMENTS)),
  /**
   * InfoPopover variant
   */
  variant: PropTypes.oneOf(Object.values(Flyout.VARIANTS)),
  /**
   * delay before show in milliseconds
   */
  delayShow: PropTypes.number,
  /**
   * delay before hide in milliseconds
   */
  delayHide: PropTypes.number,
  /**
   * style to add to the Overlay component
   */
  overlayStyle: stylePropType,
  /**
   * callback to be called on show
   */
  onShow: PropTypes.func,
  /**
   * callback to be called on hide
   */
  onHide: PropTypes.func
};
export default Object.assign(InfoPopover, {
  Arrow: Flyout.Arrow,
  Target: Flyout.Target,
  Body: Flyout.Body,
  VARIANTS: Flyout.VARIANTS,
  PLACEMENTS: Flyout.PLACEMENTS,
  TRIGGER_EVENTS: Flyout.TRIGGER_EVENTS,
  TRIGGER_TARGETS: Flyout.TRIGGER_TARGETS
});
var _StyledDiv = _styled("div").withConfig({
  displayName: "InfoPopover___StyledDiv",
  componentId: "sc-151xikh-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=InfoPopover.js.map